<template>
  <modal-card
    :title="pageTitle"
    :showActionsSpacer="false"
    class="contact-portal"
    fixed
    @close="$emit('close')"
  >
    <v-progress-linear v-if="!iframeLoaded" indeterminate color="primary" />

    <div class="test-iframe-wrap" v-show="iframeLoaded"></div>

    <template v-slot:actions>
      <v-text-field
        :value="copyLink"
        class="no-label background-dark mr-2"
        readonly
      />

      <contact-portal-link-copy
        :copyLink="copyLink"
        :contactName="contactName"
        :pid="pid"
      />
    </template>
  </modal-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { connectToChild } from '@/lib/parentConnection';
import { appRoute } from '@/globals';
import localStore from 'store';

import ContactPortalLinkCopy from '@/views/contacts/contact/ContactPortalLinkCopy.vue';

export default {
  name: 'ContactPortal',
  components: {
    ContactPortalLinkCopy,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      iframeLoaded: false,
      contactToken: '',
      currentUrl: null,
      contactPid: null,
    };
  },
  computed: {
    ...mapState('campaigns', ['campaignTest']),
    pageTitle() {
      return `${this.contactName}'s Portal`;
    },

    pid() {
      return this.contact?.con_pid || this.contactPid;
    },

    contactId() {
      return this.contact?.con_id;
    },

    contactName() {
      const { first_name = '', last_name = '', name } = this.contact || {};
      return name || `${first_name} ${last_name}`.trim() || 'Unknown';
    },

    portalHomeUrl() {
      if (!this.pid) return;

      return appRoute(`/portal/${this.pid}`, true);
    },

    copyLink() {
      return this.currentUrl || this.portalHomeUrl;
    },
  },
  watch: {
    pid() {
      this.initPortalIframe();
    },
    contactToken() {
      this.initPortalIframe();
    },
  },
  methods: {
    ...mapActions('campaigns', ['loadCampaignTest']),
    ...mapActions(['loadSingleContact', 'getContactToken']),

    async initPortalIframe() {
      await this.$nextTick();

      if (!this.contactToken || !this.pid) return;

      const iframe = document.createElement('iframe');
      iframe.setAttribute('frameBorder', 0);
      const iframeContainer = document.querySelector('.test-iframe-wrap');

      const portalAuth = {
        id: this.pid,
        token: this.contactToken,
      };

      localStore.set('portal_auth', portalAuth);

      const options = {
        url: this.portalHomeUrl,
        appendTo: iframeContainer,
        methods: {
          navigate: (url) => {
            this.setCurrentUrl(url);
          },
        },
        iframe: iframe,
      };

      try {
        const connection = await connectToChild(options);
        const portal = await connection.promise;

        if (portal) {
          portal.setAuth(portalAuth);
        }
      } catch (e) {
        console.error('Error loading campaign test', e);
      } finally {
        this.iframeLoaded = true;
      }
    },

    async fetchToken() {
      if (!this.contactId) return;

      this.contactToken = await this.getContactToken(this.contactId);
    },

    async checkContactPid() {
      if (this.contactPid || !this.contactId) return;

      const contact = await this.loadSingleContact(this.contactId);
      this.contactPid = contact.con_pid;
    },

    setCurrentUrl(url) {
      if (!url || this.portalHomeUrl.endsWith(url) || url === '/portal/') {
        this.currentUrl = this.portalHomeUrl;
        return;
      }

      const cleanUrl = url.replace(/\/portal/g, '');
      this.currentUrl = `${this.portalHomeUrl}${cleanUrl}`;
    },

    clearPreviousPortalAuth() {
      localStore.remove('portal_auth');
    },
  },
  mounted() {
    this.clearPreviousPortalAuth();
    this.fetchToken();
    this.checkContactPid();
  },
};
</script>

<style lang="less">
.test-iframe-wrap {
  margin: 0 auto;
  height: 100%;
  width: 100%;
  position: relative;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: #000;
  }
}
</style>
